import { Box, Divider, Paper, Typography } from '@mui/material';
import { BASE_COLORS } from 'lib/functions/const/colors';
import { formatDate, getChileUtcOffset } from 'lib/functions/utils';
import React from 'react';

import { PulsingDot, StatusChip } from './atoms';
import { EvaluationDetailExtended } from './types';

const chileOffsetInMs = getChileUtcOffset() * 60 * 60 * 1000;

interface EvaluationInfoCardProps {
  evaluation: EvaluationDetailExtended;
  hasWaiting: boolean;
  state?: string;
}

const InfoField: React.FC<{ label: string; value: string; sub?: string }> = ({
  label,
  value,
  sub,
}) => (
  <Box>
    <Typography
      variant="caption"
      sx={{
        color: 'text.secondary',
        fontSize: '0.68rem',
        textTransform: 'uppercase',
        letterSpacing: '0.4px',
        display: 'block',
      }}
    >
      {label}
    </Typography>
    <Typography
      variant="body2"
      sx={{ fontWeight: 600, fontSize: '0.85rem', lineHeight: 1.3 }}
    >
      {value}
    </Typography>
    {sub && (
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ fontSize: '0.7rem' }}
      >
        {sub}
      </Typography>
    )}
  </Box>
);

const ChipField: React.FC<{ label: string; children: React.ReactNode }> = ({
  label,
  children,
}) => (
  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.4 }}>
    <Typography
      variant="caption"
      sx={{
        color: 'text.secondary',
        fontSize: '0.68rem',
        textTransform: 'uppercase',
        letterSpacing: '0.4px',
      }}
    >
      {label}
    </Typography>
    {children}
  </Box>
);

const EvaluationInfoCard: React.FC<EvaluationInfoCardProps> = ({
  evaluation,
  hasWaiting,
  state,
}) => (
  <Paper
    sx={{
      p: 2.5,
      border: '1px solid #e0e0e0',
      boxShadow: 'none',
      borderRadius: 2,
    }}
  >
    <Box
      sx={{ display: 'flex', flexWrap: 'wrap', gap: 2.5, alignItems: 'center' }}
    >
      <InfoField
        label="Motor"
        value={evaluation.engine.name}
        sub={evaluation.engine.engineType}
      />
      <Divider orientation="vertical" flexItem />
      <InfoField label="Evaluador" value={evaluation.reviewerName || 'N/A'} />
      <Divider orientation="vertical" flexItem />
      <InfoField
        label="Fecha"
        value={formatDate(
          new Date(evaluation.createdAt + chileOffsetInMs),
          'large',
        )}
      />
      <Divider orientation="vertical" flexItem />
      <ChipField label="Resultado">
        {hasWaiting && state !== 'Fallido' ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            <PulsingDot />
            <Typography
              variant="body2"
              sx={{
                fontSize: '0.78rem',
                fontWeight: 600,
                color: BASE_COLORS.amber,
              }}
            >
              Procesando...
            </Typography>
          </Box>
        ) : (
          <StatusChip
            label={evaluation.statusNode?.name ?? 'Sin resultado'}
            severity={evaluation.statusNode?.severity}
          />
        )}
      </ChipField>
      {evaluation.state && (
        <>
          <Divider orientation="vertical" flexItem />
          <ChipField label="Estado">
            <StatusChip label={evaluation.state} />
          </ChipField>
        </>
      )}
    </Box>
  </Paper>
);

export default EvaluationInfoCard;
